// ─── ErrorBoundary ───
// Top-level React error boundary — the OUTER backstop wrapping <RouterProvider>.
// Catches render errors that the router's own defaultErrorComponent can't (errors
// in the providers, the router instance itself, or anything above the route tree).
// Without it, such a throw white-screens the whole app with no recovery.
//
// React error boundaries MUST be class components (no hook equivalent for
// getDerivedStateFromError / componentDidCatch). The presentational + i18n'd
// fallback lives in ErrorFallback (a function component) so this stays minimal.
//
// Observability: componentDidCatch logs to console.error, which the Azure Static
// Web Apps / App Insights browser SDK forwards once wired (owner-gated — see the
// Phase 10 follow-up). Until then it at least surfaces in the browser console and
// local dev.

import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorFallback } from './ErrorFallback';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Structured, greppable log line for observability (App Insights picks up
    // console.error). Never throws from here.
    console.error('[ErrorBoundary] uncaught render error', error, info.componentStack);
  }

  private readonly reset = (): void => {
    this.setState({ error: null });
  };

  render(): ReactNode {
    if (this.state.error) {
      return <ErrorFallback error={this.state.error} onRetry={this.reset} />;
    }
    return this.props.children;
  }
}
